import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { History as HistoryIcon, Clock, Landmark } from 'lucide-react';
import history from '../../assets/images/vjhist.jpg';
import history2 from '../../assets/images/VJ-Jubilee.jpg';

const milestones = [
  {
    year: '1879',
    title: 'Founding in Asansol',
    desc: 'Founded as a seminary in Asansol, establishing the roots of systematic theological formation in India.'
  },
  {
    year: '1889',
    title: 'St. Mary’s College, Kurseong',
    desc: 'Transferred to the serene Himalayas of Kurseong (Darjeeling), St. Mary’s College became exclusively a Jesuit Theologate.'
  },
  {
    year: '1932',
    title: 'Pontifical Faculty Status',
    desc: 'Erected by the Holy See as an ecclesiastical faculty with authority to confer canonical academic degrees in Theology.'
  },
  {
    year: '1971–1972',
    title: 'Historic Shift to Delhi',
    desc: 'Under the visionary leadership of Fr. Pedro Arrupe SJ, the theologate relocated to the national capital to engage directly with the socio-cultural realities of the people.'
  },
  {
    year: '1978',
    title: 'Inclusive Expansion & Co-education',
    desc: 'Opened doors to diocesan seminarians, religious sisters, and the first woman lay theology student.'
  },
  {
    year: '2022',
    title: 'Veritatis Gaudium Approval',
    desc: 'Updated Statutes approved and ratified by the Congregation for Catholic Education (Rome) in accordance with the apostolic constitution Veritatis Gaudium.'
  }
];

export default function History() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="vj-history-page py-5">
      <Helmet>
        <title>Our History - Vidyajyoti College of Theology</title>
        <meta name="description" content="Explore the 150+ year legacy of Vidyajyoti College of Theology from Asansol (1879) and Kurseong to Delhi." />
      </Helmet>

      <div className="container">
        {/* Page Header */}
        <div className="text-center mx-auto pb-5" style={{ maxWidth: '750px' }}>
          <span className="text-burgundy fw-bold text-uppercase small" style={{ letterSpacing: '0.1em' }}>
            150+ YEARS OF SCHOLARLY EXCELLENCE
          </span>
          <h1 className="vj-card-title display-6 fw-bold mt-1 text-dark-main">
            History of Vidyajyoti
          </h1>
          <p className="text-muted mt-2">
            A journey rooted in faith, academic rigor, and solidarity with the marginalized since 1879.
          </p>
          <div className="mx-auto mt-2" style={{ width: '60px', height: '3px', background: '#79031d', borderRadius: '2px' }} />
        </div>

        {/* Jubilee Banner Card */}
        <div className="row g-4 align-items-center mb-5">
          <div className="col-lg-6">
            <div className="vj-modern-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center" style={{ borderLeft: '5px solid #1a2b56' }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge">
                  <HistoryIcon size={22} />
                </div>
                <h3 className="fw-bold fs-4 text-dark mb-0">Roots in Asansol & Kurseong</h3>
              </div>
              <p className="text-muted" style={{ lineHeight: '1.8' }}>
                Vidyajyoti has a rich history spanning over a century and a half. Founded as a seminary in 1879 in Asansol, it was transferred in 1889 to Kurseong in the Darjeeling district, taking the name of <strong>St. Mary’s College</strong>.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8' }}>
                In 1932, the Holy See officially erected it as a Pontifical Faculty with authority to confer academic degrees in Theology.
              </p>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="vj-modern-card overflow-hidden shadow-sm">
              <img src={history2} alt="Vidyajyoti Jubilee" className="w-100 object-fit-cover" style={{ maxHeight: '350px' }} />
            </div>
          </div>
        </div>

        {/* Milestone Timeline Grid */}
        <div className="mb-5">
          <h3 className="fw-bold fs-4 text-dark-main text-center mb-4">Historical Milestones</h3>
          <div className="row g-4">
            {milestones.map((m, idx) => (
              <div className="col-lg-4 col-md-6 d-flex" key={idx}>
                <div className="vj-modern-card p-4 w-100 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="badge bg-dark-main px-3 py-1 rounded-pill fw-bold fs-6">
                      {m.year}
                    </span>
                    <Clock size={16} className="text-muted opacity-60" />
                  </div>
                  <h5 className="fw-bold text-dark mb-2">{m.title}</h5>
                  <p className="text-muted small flex-grow-1 mb-0" style={{ lineHeight: '1.7' }}>
                    {m.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* The Delhi Era Card */}
        <div className="row g-4 align-items-center mb-5 flex-lg-row-reverse">
          <div className="col-lg-6">
            <div className="vj-modern-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center" style={{ borderLeft: '5px solid #79031d' }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge burgundy">
                  <Landmark size={22} />
                </div>
                <h3 className="fw-bold fs-4 text-dark mb-0">The Delhi Era & Fr. Pedro Arrupe</h3>
              </div>
              <p className="text-muted" style={{ lineHeight: '1.8' }}>
                Fr. Pedro Arrupe SJ, Superior General of the Society of Jesus, decided to shift the theologate from the secluded Himalayas to the heart of the national capital. On <strong>January 17, 1972</strong>, classes began in Delhi with 62 theologians and 13 professors, alongside the transfer of 70,000 library volumes.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8' }}>
                Today, Vidyajyoti stands as a premier institute of contextual theologizing, forming men and women for ecclesiastical and social leadership across India and Asia.
              </p>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="vj-modern-card overflow-hidden shadow-sm">
              <img src={history} alt="Historical Archive" className="w-100 object-fit-cover" style={{ maxHeight: '350px' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
